import gsap, { SplitText } from "@/lib/gsap";
import React, { useEffect, useRef } from "react";
import Figure from "./Figure";
import {
  Container,
  Excerpt,
  Heading,
  TextContainer,
  InnerWrapper,
  OuterWrapper,
} from "./styles";
import { ButtonsProps } from "./Buttons/Buttons";
import Buttons from "./Buttons";
import _SplitText from "gsap/SplitText";
import { debounce } from "lodash";
import { ExcerptWrapper } from "@/components/Marketing/PageBuilder/SectionWithIllustrations/styles";
import SimpleRichText from "@/components/SimpleRichText";

type Props = {
  index: number;
  heading?: any;
  excerpt?: any;
  excerptPortable?: Record<string, any>;
  image: {
    format?: any;
    position?: any;
    asset?: any;
  };
  buttons?: ButtonsProps["buttons"];
};

const Illustration: React.FC<Props> = (props) => {
  const { index, heading, excerpt, excerptPortable, buttons, image } = props;
  const { position, format } = image || {};
  const imageIsVertical = format === "portrait";

  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const excerptRef = useRef<HTMLParagraphElement>(null);
  const excerptWrapperRef = useRef<HTMLParagraphElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);

  const headingTextRef = useRef<_SplitText | null>(null);
  const excerptTextRef = useRef<_SplitText | null>(null);

  useEffect(() => {
    const revertText = debounce(() => {
      if (headingTextRef.current) {
        headingTextRef.current?.revert();
      }

      if (excerptTextRef.current) {
        excerptTextRef.current?.revert();
      }
    }, 30);

    // Create closure around mutable refs
    const tweensToKill: any = [];
    const heading = headingRef.current;
    const excerpt = excerptRef.current;
    const imageWrapper = imageWrapperRef.current;
    const buttons = buttonsRef.current;
    const excerptWrapper = excerptWrapperRef.current;

    const prepareAnimation = () => {
      if (heading) {
        headingTextRef.current = new SplitText(heading, {
          type: "lines",
          linesClass: "lines",
        });
        gsap.set(heading, { opacity: 1.0 });
        tweensToKill.push(headingTextRef.current.lines);
      }

      if (excerpt) {
        excerptTextRef.current = new SplitText(excerpt, {
          type: "lines",
          linesClass: "lines",
        });
        const excerptText = excerptTextRef.current;
        gsap.set(excerpt, { opacity: 1.0 });
        tweensToKill.push(excerptText.lines);
      }

      window.addEventListener("resize", revertText);

      if (buttons) {
        gsap.set(buttons, { opacity: 1.0 });
      }
    };

    prepareAnimation();

    const runAnimation = () => {
      if (heading && headingTextRef.current) {
        gsap.to(headingTextRef.current.lines, {
          opacity: 1.0,
          y: 0,
          stagger: { amount: 0.3 },
          scrollTrigger: {
            trigger: heading,
            toggleActions: "play pause resume reverse",
            start: "top 75%",
          },
        });
      }

      if (excerpt && excerptTextRef.current) {
        gsap.to(excerptTextRef.current.lines, {
          opacity: 1.0,
          y: 0,
          stagger: { amount: 0.3 },
          scrollTrigger: {
            trigger: excerpt,
            toggleActions: "play pause resume reverse",
            start: "top 75%",
          },
        });
      }

      if (excerptWrapper) {
        gsap.to(excerptWrapper, {
          opacity: 1.0,
          y: 0,
          scrollTrigger: {
            trigger: excerptWrapper,
            toggleActions: "play pause resume reverse",
            start: "top 75%",
          },
        });
      }

      if (buttons) {
        gsap.to(buttons, {
          opacity: 1.0,
          translateY: 0,
          scrollTrigger: {
            trigger: buttons,
            toggleActions: "play pause resume reverse",
            start: "top 75%",
          },
        });
      }

      if (imageWrapper){
        tweensToKill.push(imageWrapper);
        gsap.to(imageWrapper, {
          opacity: 1.0,
          y: 0,
          scrollTrigger: {
            trigger: imageWrapper,
            toggleActions: "play pause resume reverse",
            start: "top 75%",
          },
        });
      }
    };
    runAnimation();

    return function cleanup() {
      window.removeEventListener("resize", revertText);
      gsap.killTweensOf(tweensToKill);
    };
  }, []);

  return (
    <OuterWrapper>
      <InnerWrapper $index={index} $position={position}>
        <Container $imageIsVertical={imageIsVertical}>
          <Figure
            ref={imageWrapperRef}
            image={image}
            isVertical={imageIsVertical}
          />
          <TextContainer>
            {heading && <Heading ref={headingRef}>{heading}</Heading>}
            {excerpt && (
              <Excerpt
                ref={excerptRef}
                dangerouslySetInnerHTML={{
                  __html: excerpt.replace(/\n/g, "<br />"),
                }}
              ></Excerpt>
            )}
            {excerptPortable && (
              <ExcerptWrapper ref={excerptWrapperRef}>
                <SimpleRichText body={excerptPortable} />
              </ExcerptWrapper>
            )}
            {buttons && <Buttons ref={buttonsRef} buttons={buttons} />}
          </TextContainer>
        </Container>
      </InnerWrapper>
    </OuterWrapper>
  );
};

export default Illustration;
